import { useEffect, useState } from "react";
import { useAirPlay } from "src/components/util/useAirPlay";
import { useGoogleCast } from "src/components/util/useGoogleCast";
import Styles from "./CastControls.module.scss";

// Minimal shape of the video.js player we touch here.
interface VjsLike {
  el(): HTMLElement;
  currentTime?: () => number;
  pause?: () => void;
}

// Standard Chromecast glyph.
const CastIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11zM21 3H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
  </svg>
);

// Standard AirPlay glyph (screen + upward triangle).
const AirPlayIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 22h12l-6-6zM21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4v-2H3V5h18v12h-4v2h4c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
  </svg>
);

// Renders Cast / AirPlay buttons over a video.js player. Each button only appears when the
// running browser has a reachable receiver (Cast in Chrome, AirPlay in Safari), so this is an
// empty overlay everywhere else.
export default function CastControls({
  player,
  src,
  poster,
  title,
  isLive,
}: {
  player: VjsLike;
  src: string;
  poster?: string;
  title?: string;
  isLive?: boolean;
}) {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);

  // Grab the underlying media element AirPlay drives. video.js renders a <video> for its tech.
  useEffect(() => {
    setVideo(player.el().querySelector("video"));
  }, [player]);

  const airplay = useAirPlay(video);
  const cast = useGoogleCast(src, { title, poster, isLive });

  if (!airplay.available && !cast.available) return null;

  const handleCast = () => {
    if (cast.casting) {
      cast.stopCasting();
      return;
    }
    // Move playback to the TV: pause the local player and resume from where the viewer was.
    player.pause?.();
    cast.startCasting(player.currentTime?.() ?? 0);
  };

  return (
    <div className={Styles.castBar}>
      {cast.available && (
        <button
          type="button"
          className={cast.casting ? `${Styles.castButton} ${Styles.active}` : Styles.castButton}
          onClick={handleCast}
          title={cast.casting ? "Stop casting" : "Cast to Chromecast"}
          aria-label={cast.casting ? "Stop casting" : "Cast to Chromecast"}
        >
          <CastIcon />
        </button>
      )}
      {airplay.available && (
        <button
          type="button"
          className={airplay.active ? `${Styles.castButton} ${Styles.active}` : Styles.castButton}
          onClick={airplay.showPicker}
          title="AirPlay (Apple TV / LG TV)"
          aria-label="AirPlay to Apple TV or LG TV"
        >
          <AirPlayIcon />
        </button>
      )}
    </div>
  );
}
